import Styles from './styles.module.css';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { PDFDocument } from "pdf-lib";
import { IoArrowDownOutline } from "react-icons/io5";
import Item from "./1.item/index";
import SchoolSearch from "./search/index";
import Stores from "./stores/index";
import Graduation, { GraduationDoc, graduationCompanyId } from "../9.items/graduation/index";
import SchoolJobPostings from "../9.items/2.jobPostings/index";
import { arrayUnion, collection, doc, getDoc, getDocs, getFirestore, onSnapshot, query, updateDoc, where, } from 'firebase/firestore';
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { fetchJobPostingPdf, jobPostingYearsForSchool } from "../0.functions/jobPostingAsset";
import ScanImport from "./scan/index";
import { useFitTable } from "../0.functions/useFitTable";
import Loading from "../9.items/loading/index";

type SchoolItemProps = {
  searchToggle: boolean;
  setSearchToggle: React.Dispatch<React.SetStateAction<boolean>>;
  schoolId: string;
  schoolName: string;
};

const School: React.FC<SchoolItemProps> = ({ searchToggle, setSearchToggle, schoolId, schoolName }) => {
  
  const db = getFirestore();

  const renderCount = useRef(0);
  renderCount.current += 1;

  const [ toggle, setToggle ] = useState({ type: "",  id: "", name: "" });
  const [ download, setDownload ] = useState<string[]>([]);

  const [ orderStoreToggle, setOrderStoreToggle ] = useState(false);
  const [ orderGraduateToggle, setOrderGraduateToggle ] = useState(false);

  const [ selectedIds, setSelectedIds ] = useState<string[]>([]);
  const [ allChecked, setAllChecked ] = useState(false);

  const [ business, setBusiness ] = useState("");
  const [ targetSelect, setTargetSelect ] = useState("");

  const [ searchText, setSearchText ] = useState("");
  const [ searchPref, setSearchPref ] = useState("");
  const [ searchSalary, setSearchSalary ] = useState("");
  const [ searchType, setSearchType ] = useState("");
  const [ searchHashTags, setSearchHashTags ] = useState<string[]>([]);

  const [ save, setSave ] = useState<string[]>([]);
  const [ block, setBlock ] = useState<string[]>([]);

  const [ print, setPrint ] = useState<string[]>([]);
  const [ recruitList, setRecruitList ] = useState<any[]>([]);
  // 求人一覧を最初に読み込み終えたか(それまでは「該当する求人がございません」ではなく読み込み中を出す)
  const [ recruitLoaded, setRecruitLoaded ] = useState<boolean>(false);

  const [ printStatus, setPrintStatus ] = useState<boolean>(false);

  // 受け取った求人票PDFをドラッグ&ドロップでデジタル化する取込画面。
  // 保存先は scans/ コレクションのみで、既存の求人一覧（searchIndex）には影響しない。
  const [ scanOpen, setScanOpen ] = useState<boolean>(false);
  
  const searchCondition = useMemo(
    () => ({
      business: business,
      target: targetSelect,
      text: searchText,
      pref: searchPref,
      salary: searchSalary,
      type: searchType,
      hashTags: searchHashTags,
    }),
    [business, searchText, searchPref, searchSalary, searchType, searchHashTags, targetSelect]
  );

  // 検索条件の数(ハッシュタグは1つずつ数える)と、まとめて戻す処理
  const activeConditionCount =
    [business, targetSelect, searchText.trim(), searchPref, searchSalary, searchType].filter(Boolean).length
    + searchHashTags.length;
  // 絞り込み中の条件(バーの下に「フィルター：」として出し、×で1つずつ外せる)
  const activeFilters: { key: string; label: string; clear: () => void }[] = [
    ...(targetSelect ? [{ key: 'target', label: `卒業時年齢：${targetSelect}`, clear: () => setTargetSelect('') }] : []),
    ...(business ? [{ key: 'business', label: `職種：${business}`, clear: () => setBusiness('') }] : []),
    ...(searchText.trim() ? [{ key: 'text', label: `フリーワード：${searchText.trim()}`, clear: () => setSearchText('') }] : []),
    ...(searchPref ? [{ key: 'pref', label: `都道府県：${searchPref}`, clear: () => setSearchPref('') }] : []),
    ...(searchSalary ? [{ key: 'salary', label: `給与：${searchSalary}万円以上`, clear: () => setSearchSalary('') }] : []),
    ...(searchType ? [{ key: 'type', label: `サロンタイプ：${searchType}`, clear: () => setSearchType('') }] : []),
    ...searchHashTags.map((tag) => ({ key: `tag-${tag}`, label: `#${tag}`, clear: () => setSearchHashTags((prev) => prev.filter((t) => t !== tag)) })),
  ];
  const resetSearch = () => {
    setBusiness('');
    setTargetSelect('');
    setSearchText('');
    setSearchPref('');
    setSearchSalary('');
    setSearchType('');
    setSearchHashTags([]);
  };

  useEffect(() => {
    if (!schoolId) return;

    const loadData = async () => {
      // 卒業生の人数は graduation を購読して別に数える(graduateCountMap)。ここでは0で読み込む
      await getRecruitList({});
      setRecruitLoaded(true);
    };

    loadData();

    const schoolRef = doc(db, "schools", schoolId);

    const unsubscribe = onSnapshot(schoolRef, (schoolSnap) => {
      if (!schoolSnap.exists()) {
        setRecruitList([]);
        return;
      }

      const recommendList: string[] = schoolSnap.data()?.recommendList ?? [];
      setSave(recommendList);
      const blockList: string[] = schoolSnap.data()?.blockList ?? [];
      setBlock(blockList);
      const print: string[] = schoolSnap.data()?.print_2027 ?? [];
      setPrint(print);
      const hidden: string[] = schoolSnap.data()?.hiddenGraduationIds ?? [];
      setHiddenGraduationIds(hidden);
    });

    return unsubscribe;

  }, [schoolId]);

  // 卒業生(graduation)。サロンが登録したもの(companyId)と、この学校が追加したもの
  // (addedBySchoolId・schoolCompanyId)の両方。学校の画面で非表示にしたサロンの登録分は数えない。
  const [ graduationDocs, setGraduationDocs ] = useState<GraduationDoc[]>([]);
  const [ hiddenGraduationIds, setHiddenGraduationIds ] = useState<string[]>([]);

  useEffect(() => {
    if (!schoolName) return;
    return onSnapshot(
      query(collection(db, "graduation"), where("school", "==", schoolName)),
      (snap) => setGraduationDocs(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }))),
      (error) => console.error("graduation fetch error:", error),
    );
  }, [schoolName]);

  const visibleGraduations = useMemo(
    () => graduationDocs.filter((g) => graduationCompanyId(g, schoolId) && !hiddenGraduationIds.includes(g.id)),
    [graduationDocs, hiddenGraduationIds, schoolId],
  );

  const graduateCountMap = useMemo(() => {
    const map: Record<string, number> = {};
    visibleGraduations.forEach((g) => {
      const companyId = graduationCompanyId(g, schoolId);
      map[companyId] = (map[companyId] ?? 0) + 1;
    });
    return map;
  }, [visibleGraduations, schoolId]);

  const getRecruitList = async (
    graduateCountMap: Record<string, number>
  ) => {
    try {
      // デモ/閲覧用アカウントは、送付先(sending_2027 / sending_2028)に関わらずsearchIndexを全件表示する。
      // recruitStatus(有料求人ページ公開)/jobPostingsStatus(求人票PDF公開)は別々のフラグで、
      // 求人票のみ・求人ページのみの片方だけ立っている会社もあるため、どちらでも絞り込まない。
      const DEMO_SCHOOL_IDS = [
        "ZuxAJPg4JIilqIhvJRdB", // さんぽう
        "nFhpYt8axmLN0jxlTy7W", // 閲覧用美容専門学校
        "D1CPXkL7Zg2iTwxdiBfh", // TEST専門学校(動作確認用 school-test@memorii-works.com)
      ];
      const isDemoSchool = DEMO_SCHOOL_IDS.includes(schoolId);

      // 送付先は年度ごとに別フィールド(sending_2027 / sending_2028)に入っているため、
      // どちらかの年度でこの学校が送付先になっている求人を表示する。
      // 両方の年度で送付先になっている求人は1件にまとめる(ドキュメントIDで重複排除)。
      const SENDING_YEARS = ["2027", "2028"];

      const snaps = isDemoSchool
        ? [await getDocs(query(collection(db, "searchIndex")))]
        : await Promise.all(
            SENDING_YEARS.map((year) =>
              getDocs(
                query(
                  collection(db, "searchIndex"),
                  where(`sending_${year}`, "array-contains", schoolId)
                )
              )
            )
          );

      const jobDocs = Array.from(
        new Map(
          snaps.flatMap((snap) => snap.docs).map((d) => [d.id, d] as const)
        ).values()
      );

      if (jobDocs.length === 0) {
        setRecruitList([]);
        return;
      }

      const jobs: any[] = [];

      jobDocs.forEach((jobSnap) => {
        const data = jobSnap.data() as any;

        const hashTags = Array.from(
          new Set<string>([
            ...(data.target ?? []),
            ...(data.business ?? []),
            ...(data.employmentDaysOff ?? []),
            ...(data.employmentInsurance ?? []),
            ...(data.hashTagsBenefit ?? []),
            ...(data.hashTagsEducation ?? []),
            ...(data.hashTagsWorkStyle ?? []),
            ...(data.salaryTags ?? []),
            ...(data.searchKeywords ?? []),
            ...(data.searchSummary ?? []),
            ...(data.storeTags ?? []),
            ...(data.storeType ?? []),
            ...(data.pentagonHash ?? []),
          ])
        );


        const companyId = data.companyId;

        jobs.push({
          id: jobSnap.id,
          title: data.title ?? "",
          name: data.name ?? "",
          image: data.image ?? "",
          searchSummary: data.searchSummary ?? "",

          hashTags,
          storeTags: data.storeTags ?? [],
          searchKeywords: data.searchKeywords ?? [],

          graduate: graduateCountMap[companyId] ?? 0,

          recruitStatus: data.recruitStatus ?? false,
          jobPostingsStatus: data.jobPostingsStatus ?? false,
          pamphlet: data.pamphlet,
          randomValue: data.randomValue ?? 0,
          companyId: data.companyId,
          storeCount: data.storeCount,
          searchCode: data.searchCode,
          jobPostingYears: jobPostingYearsForSchool(data, schoolId),
        });
      });

      setRecruitList(jobs);
    } catch (error) {
      console.error("getRecruitList error:", error);
      setRecruitList([]);
    }
  };

  const filterJobs = (jobs: any[], condition: any): any[] => {

    const conditionTags: string[] = [
      condition.business,
      condition.target,
      condition.pref,
      condition.salary,
      condition.type,
      ...(condition.hashTags ?? []),
    ].filter(Boolean);

    return jobs.filter((job) => {
      const jobTags: string[] = job.hashTags ?? [];
      const searchSummary = job.searchSummary;

      if (condition.text) {
        const keyword = condition.text.toLowerCase();
        const hit = Array.isArray(searchSummary) && searchSummary.join(" ").toLowerCase().includes(keyword);
        if (!hit) return false;
      }
        
      // タグ検索
      if (conditionTags.length > 0) {
        const hasAll = conditionTags.every((tag) =>
          jobTags.includes(tag)
        );

        if (!hasAll) return false;
      }

      return true;
    });
  };

  /* =========================
  * 検索結果
  * ========================= */
  const filteredRecruitList = useMemo(() => {
    const withGraduates = recruitList.map((r) => ({ ...r, graduate: graduateCountMap[r.companyId] ?? 0 }));
    return filterJobs(withGraduates, searchCondition);
  }, [recruitList, searchCondition, graduateCountMap]);

  const displayRecruitList = useMemo(() => {
    if (!orderStoreToggle && !orderGraduateToggle) return filteredRecruitList;

    if (orderStoreToggle) {
      return [...filteredRecruitList].sort(
        (a, b) => (b.storeCount ?? 0) - (a.storeCount ?? 0)
      );
    }

    if (orderGraduateToggle) {
      return [...filteredRecruitList].sort(
        (a, b) => (b.graduate ?? 0) - (a.graduate ?? 0)
      );
    }

    return filteredRecruitList

  }, [orderStoreToggle, orderGraduateToggle, filteredRecruitList]);

  /* =========================
  * 表示用リスト（最終）
  * ========================= */

  const displayedRecruitList = useMemo(() => {
    if (!save || save.length === 0) return displayRecruitList;

    return [...displayRecruitList].sort((a, b) => {

      const aSaved = save.includes(a.id);
      const bSaved = save.includes(b.id);

      if (aSaved === bSaved) return 0;
      return aSaved ? -1 : 1;
    });
  }, [displayRecruitList, save]);

  const handleSelectAll = () => {
    if (!allChecked) {
      setSelectedIds(displayedRecruitList.map((r) => r.id));
    } else {
      setSelectedIds([]);
    }
    setAllChecked(!allChecked);
  };

  const handleMergeAndDownload = async () => {
    if (selectedIds.length === 0) return;

    setPrintStatus(true);

    try {
      const mergedPdf = await PDFDocument.create();

      for (const id of selectedIds) {
        try {
          // この学校が送付先に入っている年度の求人票を優先して取得する
          const years = recruitList.find((r) => r.id === id)?.jobPostingYears;
          const response = await fetchJobPostingPdf(id, years, 20);
          if (!response.ok) {
            console.error("fetch failed:", id);
            continue;
          }

          const contentType = response.headers.get("content-type");
          if (!contentType?.includes("pdf")) {
            console.error("not pdf:", id, contentType);
            continue;
          }

          const pdfBytes = await response.arrayBuffer();

          const pdf = await PDFDocument.load(pdfBytes, {
            ignoreEncryption: true,
          });

          const normalizedBytes = await pdf.save();
          const normalizedPdf = await PDFDocument.load(normalizedBytes);

          const pages = await mergedPdf.copyPages(
            normalizedPdf,
            normalizedPdf.getPageIndices()
          );

          pages.forEach((page) => mergedPdf.addPage(page));
        } catch (e) {
          console.error("merge failed:", id, e);
        }
      }

      if (mergedPdf.getPageCount() === 0) {
        alert("PDFの取得に失敗しました。時間をおいて再度お試しください。");
        return;
      }

      const mergedPdfBytes = await mergedPdf.save();
      const arrayBuffer = new Uint8Array(mergedPdfBytes).buffer;

      const blob = new Blob([arrayBuffer], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = "求人票_まとめ.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();

      URL.revokeObjectURL(blobUrl);

      setDownload((prev) =>
        Array.from(new Set([...prev, ...selectedIds]))
      );

      const ref = doc(db, "schools", schoolId);
      await updateDoc(ref, {
        print_2027: arrayUnion(...selectedIds),
      });
      setSelectedIds([]);
    } catch (e) {
      console.error("handleMergeAndDownload error:", e);
      alert("PDFのダウンロードに失敗しました。担当者にご連絡ください。");
    } finally {
      setPrintStatus(false);
    }
  };

  // 表は画面の高さに合わせ、見出しを残して行だけをスクロールする
  const tableRef = useFitTable<HTMLDivElement>();

  return (
    <>
    {/* 検索中は、条件の数とまとめてリセットするボタンを一覧の上に出す */}
    {activeConditionCount > 0 && (
      <div className={Styles.searchActiveBar}>
        <div className={Styles.searchActiveRow}>
          <span>{activeConditionCount}件の検索条件で絞り込み中</span>
          <button type="button" onClick={() => setSearchToggle(true)}>条件を変更</button>
          <button type="button" className={Styles.searchResetBtn} onClick={resetSearch}>検索条件をリセット</button>
        </div>
        <div className={Styles.searchFilters}>
          <span className={Styles.searchFiltersLabel}>フィルター：</span>
          {activeFilters.map((f) => (
            <span key={f.key} className={Styles.searchFilterChip}>
              {f.label}
              <button type="button" aria-label={`「${f.label}」の条件を外す`} onClick={f.clear}>×</button>
            </span>
          ))}
        </div>
      </div>
    )}
    <button onClick={handleMergeAndDownload} className={Styles.print} style={selectedIds.length === 0 ? {backgroundColor : "#EBEBEB", color: "#999999"} : {}}>
      {printStatus ? <AiOutlineLoading3Quarters className={Styles.spinner} /> : "求人票を印刷する"}</button>

    {/* 求人票スキャン取込。analyzeRecruitScan（Functions）と scans/ の Firestore ルールが
        本番に必要。未デプロイ状態で表示するとPDF投入時に解析呼び出しでエラーになる。 */}
    {/* <button onClick={() => setScanOpen(true)} className={Styles.scanImport}>求人票を取り込む</button> */}

    <div className={Styles.works_service} ref={tableRef}>

      <div className={Styles.recruit_table_nav}>
        <input type="checkbox" className={Styles.recruit_table_nav_1} checked={allChecked} onChange={handleSelectAll} />
        <h3 className={Styles.recruit_table_nav_2}>企業名</h3>
        <h3 className={Styles.recruit_table_nav_3}>都道府県</h3>
        <h3 className={Styles.recruit_table_nav_6}>店舗数
          <IoArrowDownOutline className={Styles.order_icon} style={orderStoreToggle ? { color: "var(--mw-brand)" } : {}}
          onClick={() => { setOrderGraduateToggle(false); setOrderStoreToggle(prev => !prev); }} />
        </h3>
        <h4 className={Styles.recruit_table_nav_4}>PR文章</h4>
        <h3 className={Styles.recruit_table_nav_7}>卒業生 
          <IoArrowDownOutline className={Styles.order_icon} style={orderGraduateToggle ? { color: "var(--mw-brand)" } : {}}
          onClick={() => { setOrderGraduateToggle(prev => !prev); setOrderStoreToggle(false); }} />
        </h3>
        <div style={{ flex: "1" }}></div>
        <h3 className={Styles.recruit_table_nav_5}>求人票</h3>
        <h3 className={Styles.recruit_table_nav_5}>パンフ</h3>
        <h3 className={Styles.recruit_table_nav_5}>求人</h3>
        <h4 className={Styles.recruit_table_nav_5}>推奨</h4>
        <h4 className={Styles.recruit_table_nav_5}>ブロック</h4>
      </div>

      <div className="schoolTableBody">
        {!recruitLoaded ? <Loading /> : <>
        {displayRecruitList.length === 0 ? <p className={Styles.recruit_none}>該当する求人がございません。</p> : <></> }

        {displayRecruitList.map((data) => {
          return (save.includes(data.id) && !block.includes(data.id)) && (
            <div key={data.id}>
              <Item
                print={print}
                schoolId={schoolId}
                data={data}
                download={download}
                setToggle={setToggle}
                save={save}
                setSave={setSave}
                block={block}
                setBlock={setBlock}
                selectedIds={selectedIds}
                setSelectedIds={setSelectedIds}
                searchPref={searchPref}
                searchType={searchType}
              />
            </div>
          );
        })}

        {displayRecruitList.map((data) => {
          return (!save.includes(data.id) && !block.includes(data.id)) && (
            <div key={data.id}>
              <Item
                print={print}
                schoolId={schoolId}
                data={data}
                download={download}
                setToggle={setToggle}
                save={save}
                setSave={setSave}
                block={block}
                setBlock={setBlock}
                selectedIds={selectedIds}
                setSelectedIds={setSelectedIds}
                searchPref={searchPref}
                searchType={searchType}
              />
            </div>
          );
        })}
        
        <div className={Styles.block_title}>
          <h4>ブロックリスト</h4>
          <div></div>
        </div>
        
        {displayRecruitList.map((data) => {
          return block.includes(data.id) && (
            <div key={data.id}>
              <Item
                print={print}
                schoolId={schoolId}
                data={data}
                download={download}
                setToggle={setToggle}
                save={save}
                setSave={setSave}
                block={block}
                setBlock={setBlock}
                selectedIds={selectedIds}
                setSelectedIds={setSelectedIds}
                searchPref={searchPref}
                searchType={searchType}
              />
            </div>
          );
        })}
        </>}
      </div>

    </div>

    {/* search */}
    {searchToggle && (
      <SchoolSearch 
        setSearchToggle={setSearchToggle}

        business={business}
        setBusiness={setBusiness}
        targetSelect={targetSelect}
        setTargetSelect={setTargetSelect}
        searchText={searchText}
        setSearchText={setSearchText}
        searchPref={searchPref}
        setSearchPref={setSearchPref}
        searchSalary={searchSalary}
        setSearchSalary={setSearchSalary}
        searchType={searchType}
        setSearchType={setSearchType}
        searchHashTags={searchHashTags}
        setSearchHashTags={setSearchHashTags}
        activeConditionCount={activeConditionCount}
        onReset={resetSearch}
      />
    )}

    {/* href */}
    {toggle.type === "store" ?
      <Stores setToggle={setToggle} companyId={toggle.id} searchPref={searchPref} searchType={searchType} />
    : <></> }

    {toggle.type === "graduate" ?
      <Graduation
        schoolName={schoolName}
        schoolId={schoolId}
        toggle={toggle}
        setToggle={setToggle}
        graduationDocs={graduationDocs}
        hiddenGraduationIds={hiddenGraduationIds}
      />
    : <></> }

    {toggle.type === "jobPosting" ?
      <SchoolJobPostings schoolId={schoolId} setToggle={setToggle} recruitId={toggle.id} />
    :<></> }

    <ScanImport
      open={scanOpen}
      schoolId={schoolId}
      schoolName={schoolName}
      onClose={() => setScanOpen(false)} />
    </>
  );
}

export default School;
